<script>
import { generateTimeSlots } from '../helpers/businessHour';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import NextSelect from 'dashboard/components-next/select/Select.vue';

const timeSlots = generateTimeSlots(30);

const groupByPeriod = slots =>
  ['AM', 'PM']
    .map(period => ({
      label: period,
      options: slots
        .filter(s => s.endsWith(period))
        .map(s => ({ value: s, label: s })),
    }))
    .filter(g => g.options.length);

export default {
  components: {
    Icon,
    NextSelect,
  },
  props: {
    dayName: {
      type: String,
      required: true,
    },
    timeSlot: {
      type: Object,
      default: () => ({
        from: '',
        to: '',
        enabled: false,
        openAllDay: false,
        valid: false,
      }),
    },
  },
  emits: ['update'],
  computed: {
    fromTimeSlots() {
      return groupByPeriod(timeSlots);
    },
    toTimeSlots() {
      return groupByPeriod(timeSlots);
    },
    isDayEnabled: {
      get() {
        return Boolean(this.timeSlot.enabled);
      },
      set(value) {
        const newSlot = value
          ? {
              ...this.timeSlot,
              enabled: true,
              from: this.timeSlot.from || '06:00 PM',
              to: this.timeSlot.to || '08:00 AM',
              valid: true,
              openAllDay: false,
            }
          : {
              ...this.timeSlot,
              enabled: false,
              from: '',
              to: '',
              valid: false,
              openAllDay: false,
            };
        this.$emit('update', newSlot);
      },
    },
    fromTime: {
      get() {
        return this.timeSlot.from;
      },
      set(value) {
        this.$emit('update', {
          ...this.timeSlot,
          from: value,
          valid: Boolean(value && this.timeSlot.to),
        });
      },
    },
    toTime: {
      get() {
        return this.timeSlot.to;
      },
      set(value) {
        this.$emit('update', {
          ...this.timeSlot,
          to: value,
          valid: Boolean(this.timeSlot.from && value),
        });
      },
    },
    isOvernight() {
      if (!this.timeSlot.enabled || this.timeSlot.openAllDay) return false;
      if (!this.timeSlot.from || !this.timeSlot.to) return false;
      // Overnight when end label sorts "before" start in a same-day comparison via AM/PM lists index
      const fromIdx = timeSlots.indexOf(this.timeSlot.from);
      const toIdx = timeSlots.indexOf(this.timeSlot.to);
      return fromIdx >= 0 && toIdx >= 0 && toIdx <= fromIdx;
    },
    hoursLabel() {
      if (this.timeSlot.openAllDay) return '24h';
      if (this.isOvernight)
        return this.$t('AGENT_BOTS.BOT_CONFIGURATION.SCHEDULE.OVERNIGHT');
      return '';
    },
    isOpenAllDay: {
      get() {
        return this.timeSlot.openAllDay;
      },
      set(value) {
        if (value) {
          this.$emit('update', {
            ...this.timeSlot,
            enabled: true,
            from: '12:00 AM',
            to: '11:59 PM',
            valid: true,
            openAllDay: true,
          });
        } else {
          this.$emit('update', {
            ...this.timeSlot,
            enabled: true,
            from: '06:00 PM',
            to: '08:00 AM',
            valid: true,
            openAllDay: false,
          });
        }
      },
    },
  },
};
</script>

<template>
  <tr>
    <td class="ltr:pl-4 ltr:pr-3 rtl:pl-3 rtl:pr-4">
      <div class="flex items-center gap-2 min-h-16">
        <input
          v-model="isDayEnabled"
          name="enable-bot-day"
          class="m-0"
          type="checkbox"
        />
        <span class="text-body-main text-n-slate-12 font-medium">
          {{ dayName }}
        </span>
      </div>
    </td>
    <td class="py-3 ltr:pr-3 rtl:pl-3">
      <div v-if="isDayEnabled" class="flex flex-col gap-1.5">
        <div class="flex flex-wrap items-center gap-4">
          <div class="flex items-center gap-2">
            <input
              v-model="isOpenAllDay"
              name="enable-bot-open-all-day"
              class="m-0"
              type="checkbox"
            />
            <span class="text-body-main text-n-slate-12">{{
              $t('AGENT_BOTS.BOT_CONFIGURATION.SCHEDULE.ALL_DAY')
            }}</span>
          </div>
          <NextSelect
            v-model="fromTime"
            :groups="fromTimeSlots"
            :placeholder="$t('AGENT_BOTS.BOT_CONFIGURATION.SCHEDULE.CHOOSE')"
            :disabled="isOpenAllDay"
          />
          <div class="flex items-center">
            <Icon icon="i-lucide-minus size-4" />
          </div>
          <NextSelect
            v-model="toTime"
            :groups="toTimeSlots"
            :placeholder="$t('AGENT_BOTS.BOT_CONFIGURATION.SCHEDULE.CHOOSE')"
            :disabled="isOpenAllDay"
          />
        </div>
        <span v-if="isOvernight" class="text-xs text-n-slate-11">
          {{ $t('AGENT_BOTS.BOT_CONFIGURATION.SCHEDULE.OVERNIGHT_HINT') }}
        </span>
      </div>
      <span v-else class="text-body-main text-n-slate-11">
        {{ $t('AGENT_BOTS.BOT_CONFIGURATION.SCHEDULE.OFF') }}
      </span>
    </td>
    <td class="py-3 ltr:pr-3 rtl:pl-3">
      <span
        v-if="isDayEnabled && hoursLabel"
        class="label bg-n-blue-3 text-n-blue-11 text-label-small inline-block px-2 py-1 rounded-lg cursor-default whitespace-nowrap"
      >
        {{ hoursLabel }}
      </span>
    </td>
  </tr>
</template>
